import { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit, Image as ImageIcon, Plus, Search, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createCategory,
  createProduct,
  deleteCategory,
  deleteProduct,
  listCategories,
  listProducts,
  updateCategory,
  updateProduct,
  uploadProductImage
} from '../../api/marketplace';
import { getErrorMessage } from '../../api/response';
import Button from '../../components/Button';
import ConfirmDialog from '../../components/ConfirmDialog';
import FormField, { inputClassName } from '../../components/FormField';
import Modal from '../../components/Modal';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import Table from '../../components/Table';
import { LoadingState } from '../../components/PageState';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { activeStatus, getStatusConfig } from '../../utils/status';
import { currency } from '../../utils/format';
import { resizeImageFile } from '../../utils/images';

const emptyProduct = {
  category_id: '',
  name: '',
  description: '',
  sku: '',
  price: '',
  offer_price: '',
  stock: '',
  is_active: true,
  image_url: '',
  image_file: null,
  image_preview: ''
};

const emptyCategory = {
  name: '',
  description: '',
  is_active: true
};

const Productos = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productModal, setProductModal] = useState(false);
  const [categoryModal, setCategoryModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [confirm, setConfirm] = useState(null);

  const categoryById = useMemo(() => new Map(categories.map((category) => [Number(category.id), category])), [categories]);
  const debouncedSearch = useDebouncedValue(search.trim());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [productData, categoryData] = await Promise.all([
        listProducts({ search: debouncedSearch || undefined, category_id: categoryFilter || undefined, limit: 100 }),
        listCategories({ limit: 100 })
      ]);
      setProducts(productData);
      setCategories(categoryData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar tienda'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, debouncedSearch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const lowStock = products.filter((product) => Number(product.stock || 0) <= 5);

  const openProduct = (product = null) => {
    setEditingProduct(product);
    setProductForm(product ? {
      category_id: product.category_id || '',
      name: product.name || '',
      description: product.description || '',
      sku: product.sku || '',
      price: product.price || '',
      offer_price: product.offer_price || '',
      stock: product.stock || '',
      is_active: Boolean(Number(product.is_active)),
      image_url: '',
      image_file: null,
      image_preview: product.primary_image_url || ''
    } : emptyProduct);
    setProductModal(true);
  };

  const handleProductImageChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona una imagen valida');
      return;
    }

    try {
      const resizedFile = await resizeImageFile(file, {
        maxWidth: 1200,
        maxHeight: 1200,
        quality: 0.86
      });
      const previewUrl = URL.createObjectURL(resizedFile);

      setProductForm((current) => {
        if (current.image_preview?.startsWith('blob:')) {
          URL.revokeObjectURL(current.image_preview);
        }

        return {
          ...current,
          image_url: '',
          image_file: resizedFile,
          image_preview: previewUrl
        };
      });
    } catch (error) {
      toast.error('No se pudo procesar la imagen');
    } finally {
      event.target.value = '';
    }
  };

  const openCategory = (category = null) => {
    setEditingCategory(category);
    setCategoryForm(category ? {
      name: category.name || '',
      description: category.description || '',
      is_active: Boolean(Number(category.is_active))
    } : emptyCategory);
    setCategoryModal(true);
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload = {
        category_id: productForm.category_id || null,
        name: productForm.name,
        description: productForm.description || null,
        sku: productForm.sku || null,
        price: Number(productForm.price),
        offer_price: productForm.offer_price === '' ? null : Number(productForm.offer_price),
        stock: Number(productForm.stock || 0),
        is_active: productForm.is_active
      };

      let savedProduct = null;

      if (editingProduct) {
        await updateProduct(editingProduct.id, payload);
        if (productForm.image_file) {
          await uploadProductImage(editingProduct.id, productForm.image_file, {
            is_primary: true,
            alt_text: productForm.name
          });
        }
        toast.success('Producto actualizado');
      } else {
        savedProduct = await createProduct({
          ...payload,
          images: productForm.image_url ? [{ image_url: productForm.image_url, is_primary: true }] : []
        });
        if (productForm.image_file) {
          await uploadProductImage(savedProduct.id, productForm.image_file, {
            is_primary: true,
            alt_text: productForm.name
          });
        }
        toast.success('Producto creado');
      }

      setProductModal(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar el producto'));
    } finally {
      setSaving(false);
    }
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryForm);
        toast.success('Categoria actualizada');
      } else {
        await createCategory(categoryForm);
        toast.success('Categoria creada');
      }
      setCategoryModal(false);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo guardar la categoria'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirm) return;

    try {
      setSaving(true);
      if (confirm.type === 'product') {
        await deleteProduct(confirm.item.id);
        toast.success('Producto desactivado');
      } else {
        await deleteCategory(confirm.item.id);
        toast.success('Categoria desactivada');
      }
      setConfirm(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo completar la accion'));
    } finally {
      setSaving(false);
    }
  };

  const productColumns = [
    {
      key: 'name',
      label: 'Producto',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 overflow-hidden rounded-md bg-gray-100">
            {row.primary_image_url ? <img src={row.primary_image_url} alt="" className="h-full w-full object-cover" /> : null}
          </div>
          <div>
            <p className="font-semibold text-gray-900">{row.name}</p>
            <p className="text-xs text-gray-500">{row.sku || 'Sin SKU'}</p>
          </div>
        </div>
      )
    },
    { key: 'category_id', label: 'Categoria', render: (row) => categoryById.get(Number(row.category_id))?.name || row.category_name || '-' },
    { key: 'price', label: 'Precio', render: (row) => currency(row.pricing?.final_price ?? row.price) },
    { key: 'stock', label: 'Stock', render: (row) => <span className={Number(row.stock) <= 5 ? 'font-semibold text-red-700' : ''}>{row.stock}</span> },
    {
      key: 'is_active',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(activeStatus, Number(row.is_active));
        return <StatusBadge label={status.label} color={status.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openProduct(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => setConfirm({ type: 'product', item: row })} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Desactivar">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  const categoryColumns = [
    { key: 'name', label: 'Categoria' },
    { key: 'description', label: 'Descripcion', render: (row) => row.description || '-' },
    {
      key: 'is_active',
      label: 'Estado',
      render: (row) => {
        const status = getStatusConfig(activeStatus, Number(row.is_active));
        return <StatusBadge label={status.label} color={status.color} />;
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openCategory(row)} className="rounded-md p-2 text-blue-700 hover:bg-blue-50" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={() => setConfirm({ type: 'category', item: row })} className="rounded-md p-2 text-red-700 hover:bg-red-50" title="Desactivar">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Productos"
        description="Catalogo de tienda, precios, stock, categorias e imagen principal."
        actions={
          <>
            <Button icon={Plus} variant="secondary" onClick={() => openCategory()}>Nueva categoria</Button>
            <Button icon={Plus} onClick={() => openProduct()}>Nuevo producto</Button>
          </>
        }
      />

      {lowStock.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {lowStock.length} productos tienen stock bajo o agotado.
        </div>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} className={`${inputClassName} pl-9`} placeholder="Buscar por nombre, SKU o descripcion" />
          </div>
          <select className={inputClassName} value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)}>
            <option value="">Todas las categorias</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
          </select>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? <div className="p-5"><LoadingState rows={5} /></div> : <Table columns={productColumns} data={products} emptyMessage="No hay productos registrados" />}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-4 text-lg font-semibold text-gray-950">Categorias</h2>
        <Table columns={categoryColumns} data={categories} emptyMessage="No hay categorias registradas" />
      </section>

      <Modal
        open={productModal}
        title={editingProduct ? 'Editar producto' : 'Nuevo producto'}
        onClose={() => setProductModal(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setProductModal(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="product-form" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        }
      >
        <form id="product-form" onSubmit={saveProduct} className="grid gap-4 md:grid-cols-2">
          <FormField label="Categoria">
            <select className={inputClassName} value={productForm.category_id} onChange={(event) => setProductForm({ ...productForm, category_id: event.target.value })}>
              <option value="">Sin categoria</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
          </FormField>
          <FormField label="Nombre" required>
            <input className={inputClassName} value={productForm.name} onChange={(event) => setProductForm({ ...productForm, name: event.target.value })} required />
          </FormField>
          <FormField label="SKU">
            <input className={inputClassName} value={productForm.sku} onChange={(event) => setProductForm({ ...productForm, sku: event.target.value })} />
          </FormField>
          <FormField label="Precio" required>
            <input type="number" className={inputClassName} value={productForm.price} onChange={(event) => setProductForm({ ...productForm, price: event.target.value })} required />
          </FormField>
          <FormField label="Precio oferta">
            <input type="number" className={inputClassName} value={productForm.offer_price} onChange={(event) => setProductForm({ ...productForm, offer_price: event.target.value })} />
          </FormField>
          <FormField label="Stock">
            <input type="number" className={inputClassName} value={productForm.stock} onChange={(event) => setProductForm({ ...productForm, stock: event.target.value })} />
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={String(productForm.is_active)} onChange={(event) => setProductForm({ ...productForm, is_active: event.target.value === 'true' })}>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Imagen principal">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                  {productForm.image_preview || productForm.image_url ? (
                    <img
                      src={productForm.image_preview || productForm.image_url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-7 w-7 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className={`${inputClassName} flex cursor-pointer items-center justify-center gap-2 text-gray-700 hover:bg-gray-50`}>
                    <Upload className="h-4 w-4" />
                    <span>{productForm.image_file?.name || 'Seleccionar imagen'}</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="sr-only"
                      onChange={handleProductImageChange}
                    />
                  </label>
                  {!editingProduct && (
                    <input
                      className={inputClassName}
                      value={productForm.image_url}
                      onChange={(event) => setProductForm({
                        ...productForm,
                        image_url: event.target.value,
                        image_preview: event.target.value
                      })}
                      placeholder="URL alternativa"
                      disabled={Boolean(productForm.image_file)}
                    />
                  )}
                </div>
              </div>
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField label="Descripcion">
              <textarea rows={3} className={inputClassName} value={productForm.description} onChange={(event) => setProductForm({ ...productForm, description: event.target.value })} />
            </FormField>
          </div>
        </form>
      </Modal>

      <Modal
        open={categoryModal}
        title={editingCategory ? 'Editar categoria' : 'Nueva categoria'}
        onClose={() => setCategoryModal(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setCategoryModal(false)} disabled={saving}>Cancelar</Button>
            <Button type="submit" form="category-form" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </div>
        }
      >
        <form id="category-form" onSubmit={saveCategory} className="grid gap-4">
          <FormField label="Nombre" required>
            <input className={inputClassName} value={categoryForm.name} onChange={(event) => setCategoryForm({ ...categoryForm, name: event.target.value })} required />
          </FormField>
          <FormField label="Descripcion">
            <textarea rows={3} className={inputClassName} value={categoryForm.description} onChange={(event) => setCategoryForm({ ...categoryForm, description: event.target.value })} />
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={String(categoryForm.is_active)} onChange={(event) => setCategoryForm({ ...categoryForm, is_active: event.target.value === 'true' })}>
              <option value="true">Activa</option>
              <option value="false">Inactiva</option>
            </select>
          </FormField>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.type === 'product' ? 'Desactivar producto' : 'Desactivar categoria'}
        message={`Se desactivara ${confirm?.item?.name}.`}
        confirmLabel="Desactivar"
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default Productos;
